import { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { getContent, getHijriToday } from "../lib/api";
import { Moon, Star, Sparkles } from "lucide-react";

const HIJRI_MONTH_DAYS = 30; // approx

function daysUntilHijri(today, targetMonth, targetDay) {
  // today: {hijri:{day,month:{number}}}
  if (!today) return null;
  const curMonth = parseInt(today.hijri.month.number);
  const curDay = parseInt(today.hijri.day);
  let diff = (targetMonth - curMonth) * HIJRI_MONTH_DAYS + (targetDay - curDay);
  if (diff < 0) diff += 12 * HIJRI_MONTH_DAYS;
  return diff;
}

export default function SacredNights() {
  const { t } = useApp();
  const [nights, setNights] = useState([]);
  const [today, setToday] = useState(null);

  useEffect(() => {
    getContent("sacred_nights").then(setNights);
    getHijriToday().then(setToday).catch(() => {});
  }, []);

  const ranked = useMemo(() => {
    return nights
      .map(n => ({ ...n, in_days: daysUntilHijri(today, n.month, n.day) }))
      .sort((a, b) => (a.in_days ?? 999) - (b.in_days ?? 999));
  }, [nights, today]);

  return (
    <div className="space-y-6 fade-in" data-testid="sacred-nights-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><Moon className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.sacred_nights}</h1>
          <p className="text-sm text-nour-text2">{t.sacred_nights_sub}</p>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="nights-grid">
        {ranked.map(n => (
          <article key={n.id} className="nour-card p-6 relative overflow-hidden" data-testid={`night-${n.id}`}>
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br from-emerald-200/40 to-transparent blur-2xl" />
            <div className="relative">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-arabic-quran text-2xl text-nour-primary leading-tight" dir="rtl">{n.name_ar}</div>
                  <h2 className="font-heading font-bold text-lg mt-1">{n.name_fr}</h2>
                  <p className="text-xs text-nour-text2 italic">{n.subtitle_fr}</p>
                </div>
                {n.in_days != null && (
                  <div className="text-right shrink-0">
                    <div className="text-3xl font-extrabold text-nour-primary tabular-nums">{n.in_days}</div>
                    <div className="text-[10px] uppercase tracking-wider text-nour-text2">{t.in_days}</div>
                  </div>
                )}
              </div>

              <p className="text-sm text-nour-text mt-4 leading-relaxed">{n.importance_fr}</p>

              <div className="mt-4">
                <div className="text-xs uppercase tracking-wider text-nour-text2 mb-2 flex items-center gap-1">
                  <Star className="w-3.5 h-3.5" /> {t.worship_program}
                </div>
                <ul className="space-y-1.5">
                  {n.program_fr.map((p, i) => (
                    <li key={i} className="text-sm text-nour-text flex gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-nour-primary shrink-0 mt-1" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 nour-card p-3 bg-emerald-50">
                <div className="font-arabic-quran text-lg text-right text-nour-primary" dir="rtl">{n.dua_ar}</div>
                <div className="text-sm text-nour-text mt-1">{n.dua_fr}</div>
                <div className="text-[10px] text-nour-text2 mt-1">— {n.dua_source}</div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
